import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDetailedPortfolio } from '../hooks/useDetailedPortfolio';
import MobileFullFinancial from '../components/MobileFullFinancial';
import '../styles/ios-mobile-fixes.css';
import '../styles/chrome-mobile-fixes.css';

// Mobile Debug Logger - Ultra detailed logging for mobile debugging
const mobileDebugLog = (category, message, data = {}) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isProduction = window.location.hostname === 'arrowappfinance.com';
  
  if (isMobile || isProduction) {
    console.log(`[${timestamp}] 📱 ${category.toUpperCase()}: ${message}`, data);
    
    // Also try to store in sessionStorage for debugging
    try {
      const debugLogs = JSON.parse(sessionStorage.getItem('mobileDebugLogs') || '[]');
      debugLogs.push({
        timestamp,
        category,
        message,
        data,
        url: window.location.href,
        userAgent: navigator.userAgent.substring(0, 100)
      });
      // Keep only last 50 logs
      if (debugLogs.length > 50) debugLogs.shift();
      sessionStorage.setItem('mobileDebugLogs', JSON.stringify(debugLogs));
    } catch (e) {
      console.warn('Could not store debug log:', e);
    }
  }
};

// Global debug function for mobile debugging
window.mobileDebugDump = () => {
  const logs = JSON.parse(sessionStorage.getItem('mobileDebugLogs') || '[]');
  console.log('📱 MOBILE DEBUG DUMP:', {
    logs,
    currentState: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      localStorage: {
        userToken: !!localStorage.getItem('userToken'),
        userData: !!localStorage.getItem('userData')
      },
      sessionStorage: {
        debugLogs: logs.length + ' entries'
      },
      timestamp: new Date().toISOString()
    }
  });
  return logs;
};

const MobileFinancialPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { detailedPortfolio, loading, loadDetailedPortfolio } = useDetailedPortfolio();
  const renderCount = useRef(0);
  const hasInitialized = useRef(false);
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);
  const [browserType, setBrowserType] = useState('unknown');
  const [timeoutError, setTimeoutError] = useState(false);
  const [emergencyMode, setEmergencyMode] = useState(false);

  // Debug logging with mobile-specific details
  renderCount.current += 1;
  mobileDebugLog('render', `MobileFinancialPage render #${renderCount.current}`, {
    user: !!user,
    authLoading,
    hasPortfolio: !!detailedPortfolio,
    portfolioLoading: loading,
    url: window.location.href,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    timestamp: new Date().toISOString()
  });

  // Detect Mobile Browser (Chrome, Safari, etc.)
  useEffect(() => {
    try {
      const userAgent = navigator.userAgent;
      const isIOS = /iPhone|iPad|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      const isMobile = isIOS || isAndroid || window.innerWidth <= 768;
      
      let detectedBrowser = 'unknown';
      if (isIOS) {
        if (/CriOS/.test(userAgent) || /Chrome/.test(userAgent)) {
          detectedBrowser = 'chrome-ios';
        } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
          detectedBrowser = 'safari-ios';
        } else {
          detectedBrowser = 'other-ios';
        }
      } else if (isAndroid) {
        if (/Chrome/.test(userAgent)) {
          detectedBrowser = 'chrome-android';
        } else {
          detectedBrowser = 'other-android';
        }
      }
      
      setIsMobileBrowser(isMobile);
      setBrowserType(detectedBrowser);
      
      mobileDebugLog('detection', 'Mobile browser detected', {
        isMobile,
        browserType: detectedBrowser,
        userAgent: userAgent.substring(0, 100) + '...',
        viewport: { width: window.innerWidth, height: window.innerHeight },
        isIOS,
        isAndroid,
        location: window.location.href
      });

      // Emergency mode trigger - if we're on production and things seem broken
      if (window.location.hostname === 'arrowappfinance.com' && isMobile) {
        mobileDebugLog('emergency', 'Production mobile detected - monitoring for issues');
        
        // Set emergency mode after 5 seconds if nothing loads
        setTimeout(() => {
          if (!user && !authLoading && !detailedPortfolio && !loading) {
            mobileDebugLog('emergency', 'Activating emergency mode - nothing loaded after 5s');
            setEmergencyMode(true);
          }
        }, 5000);
      }
    } catch (error) {
      mobileDebugLog('error', 'Browser detection failed', { error: error.message });
      // Fallback to basic mobile detection
      setIsMobileBrowser(window.innerWidth <= 768);
      setBrowserType('fallback');
    }
  }, [user, authLoading, detailedPortfolio, loading]);

  // Mobile Browser Fix: Add body class for modal state
  useEffect(() => {
    document.body.classList.add('modal-open');
    
    // Add mobile browser-specific fixes for rendering
    if (isMobileBrowser) {
      document.body.classList.add('mobile-browser');
      document.body.classList.add(`mobile-browser-${browserType}`);
      
      // Force repaint on mobile browsers
      document.body.style.transform = 'translateZ(0)';
      
      // Chrome iOS specific fixes
      if (browserType === 'chrome-ios') {
        document.body.style.minHeight = '100vh';
        document.body.style.minHeight = '-webkit-fill-available';
      }
    }
    
    return () => {
      document.body.classList.remove('modal-open');
      document.body.classList.remove('mobile-browser');
      document.body.classList.remove(`mobile-browser-${browserType}`);
      if (isMobileBrowser) {
        document.body.style.transform = '';
        document.body.style.minHeight = '';
      }
    };
  }, [isMobileBrowser, browserType]);

  // Timeout mechanism to catch stuck loading states
  useEffect(() => {
    const timeoutDuration = window.location.hostname === 'arrowappfinance.com' ? 10000 : 15000; // Shorter timeout for production
    const timeoutId = setTimeout(() => {
      if (authLoading || (loading && !detailedPortfolio)) {
        mobileDebugLog('timeout', `Loading timeout after ${timeoutDuration}ms`, {
          authLoading,
          loading,
          hasPortfolio: !!detailedPortfolio,
          hasUser: !!user,
          browserType
        });
        setTimeoutError(true);
      }
    }, timeoutDuration);

    return () => clearTimeout(timeoutId);
  }, [authLoading, loading, detailedPortfolio, user, browserType]);

  // Memoize the close handler to prevent recreations
  const handleClose = useMemo(() => () => {
    console.log('🔙 Navigating back');
    navigate(-1);
  }, [navigate]);

  // Mobile browser auth check and portfolio loading with debug logging
  useEffect(() => {
    mobileDebugLog('auth-effect', 'Auth effect triggered', {
      authLoading,
      hasUser: !!user,
      isMobileBrowser,
      browserType,
      hasInitialized: hasInitialized.current,
      hasPortfolio: !!detailedPortfolio,
      portfolioLoading: loading,
      currentUrl: window.location.href,
      localStorage: {
        hasToken: !!localStorage.getItem('userToken'),
        hasUserData: !!localStorage.getItem('userData')
      }
    });

    // Wait for auth to complete first
    if (authLoading) {
      mobileDebugLog('auth-loading', 'Auth still loading, waiting...');
      return;
    }
    
    if (!user) {
      const token = localStorage.getItem('userToken');
      const userData = localStorage.getItem('userData');
      
      mobileDebugLog('auth-no-user', 'No authenticated user, checking localStorage', {
        hasToken: !!token,
        hasUserData: !!userData,
        browserType
      });
      
      if (!token) {
        mobileDebugLog('auth-no-token', 'No token found, redirecting to login');
        navigate('/login', { replace: true });
        return;
      } else {
        // Simplified auth context loading
        mobileDebugLog('auth-waiting', 'Token exists but user context not loaded', {
          browserType,
          tokenLength: token ? token.length : 0
        });
        return;
      }
    }
    
    // Log production URL for debugging
    if (window.location.hostname === 'arrowappfinance.com') {
      console.log('🌐 Production environment detected:', {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port || 'default'
      });
    }
    
    // Simplified portfolio loading logic - removed complex browser-specific delays
    if (!hasInitialized.current && !detailedPortfolio && !loading && user) {
      mobileDebugLog('portfolio-load', 'Loading portfolio', { browserType });
      hasInitialized.current = true;
      
      // Simple delay for all mobile browsers
      if (isMobileBrowser) {
        setTimeout(() => {
          loadDetailedPortfolio();
        }, 300); // Reduced delay
      } else {
        loadDetailedPortfolio();
      }
    }
  }, [user, authLoading, isMobileBrowser, browserType, detailedPortfolio, loading, loadDetailedPortfolio, navigate]);

  // Show loading state while auth is loading
  if (authLoading) {
    console.log('⏳ Showing auth loading state');
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        color: '#666',
        fontSize: '16px',
        zIndex: 10001,
        WebkitTransform: 'translateZ(0)', // iOS fix
        transform: 'translateZ(0)'
      }}>
        <div>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>Loading...</div>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
        </div>
      </div>
    );
  }

  // Show timeout error if loading takes too long
  if (timeoutError) {
    console.log('⏰ Showing timeout error state');
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        color: '#666',
        fontSize: '16px',
        zIndex: 10001,
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ marginBottom: '16px', fontSize: '18px', color: '#111' }}>
          Loading is taking longer than expected
        </div>
        <div style={{ marginBottom: '24px', color: '#666', lineHeight: '1.5' }}>
          This might be due to a slow network connection or a temporary issue.
        </div>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            marginRight: '12px'
          }}
        >
          Reload Page
        </button>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '12px 24px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  // Show error state if no user after auth loading completes
  if (!user) {
    console.log('❌ No user found, should redirect to login');
    return null; // ProtectedRoute should handle this
  }

  mobileDebugLog('render-final', 'About to render MobileFullFinancial', {
    hasUser: !!user,
    hasPortfolio: !!detailedPortfolio,
    isLoading: loading,
    emergencyMode,
    browserType
  });

  // Emergency fallback mode - bypass all complex logic and render simple version
  if (emergencyMode) {
    mobileDebugLog('emergency', 'Rendering emergency fallback mode');
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#ffffff',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚨</div>
        <h2 style={{ color: '#111', marginBottom: '16px' }}>Emergency Mode</h2>
        <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.5' }}>
          The mobile financial page encountered loading issues.<br/>
          This is a simplified emergency mode.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '12px 24px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
          <button 
            onClick={() => {
              const logs = sessionStorage.getItem('mobileDebugLogs');
              console.log('📱 Debug Logs:', JSON.parse(logs || '[]'));
              alert('Debug logs printed to console');
            }}
            style={{
              padding: '12px 24px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Show Debug Logs
          </button>
        </div>
        <div style={{
          marginTop: '24px',
          fontSize: '12px',
          color: '#999',
          fontFamily: 'monospace'
        }}>
          {browserType} | {window.innerWidth}x{window.innerHeight}
        </div>
      </div>
    );
  }
  
  return (
    <>
      {/* Enhanced debug overlay for mobile browsers - ALWAYS visible on production mobile */}
      {(isMobileBrowser && (process.env.NODE_ENV === 'development' || window.location.hostname === 'arrowappfinance.com')) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          background: browserType === 'chrome-ios' ? 'rgba(0, 123, 255, 0.9)' : 'rgba(255, 0, 0, 0.9)',
          color: 'white',
          padding: '8px',
          fontSize: '11px',
          zIndex: 99999,
          pointerEvents: 'none',
          maxWidth: '100vw',
          wordWrap: 'break-word',
          fontFamily: 'monospace',
          lineHeight: '1.2'
        }}>
          📱 {browserType.toUpperCase()} | Auth: {!!user ? '✅' : '❌'} | Portfolio: {!!detailedPortfolio ? '✅' : '❌'} | Loading: {loading ? '🔄' : '✅'}<br/>
          Render #{renderCount.current} | {window.innerWidth}x{window.innerHeight} | {new Date().toLocaleTimeString()}
          <div style={{ marginTop: '4px', fontSize: '10px', opacity: 0.8 }}>
            URL: {window.location.href.split('/').pop()} | Emergency: {emergencyMode ? 'ON' : 'OFF'}
          </div>
        </div>
      )}
      <MobileFullFinancial 
        detailedPortfolio={detailedPortfolio}
        loading={loading}
        onClose={handleClose}
      />
    </>
  );
};

export default MobileFinancialPage;